import { BaseService } from './BaseService';
import type {
  AgentMemoryDefinitionDto,
  BulkApplyRequest,
  BulkApplyResultDto,
  CreateMemoryDefinitionRequest,
  CreateRuleRequest,
  CreateRuleVersionRequest,
  DecisionReconstructionDto,
  ExtractionOutboxDto,
  InjectionAuditDto,
  OutcomeReceiptDto,
  MemoryCandidateDto,
  MemoryContextResponseDto,
  MemoryDefinitionDetailDto,
  MemoryDefinitionVersionRequest,
  MemoryErasureResultDto,
  PreviewMemoryContextRequest,
  ProceduralRuleDto,
  RuleEfficacyDto,
  SessionMemoryOutcomeDto,
  TenantMemoryPolicyDto,
} from '../types/memoryModule';

/**
 * Service for the Agent Memory module's admin + governance surfaces (Memory spec §10A).
 * The runtime surface (/v1/memory/context) is deliberately absent — it is the agent harness's,
 * under a different service principal, and the frontend must never call it. Scoped-injection
 * checks go through POST /v1/memory/definitions/{id}/preview on the admin surface instead.
 */
export class MemoryModuleService extends BaseService {
  // === Admin: Agent Memory Definitions (§10A.6, §2A.7) ===

  /** The latest version of every definition in the tenant, drafts included. */
  async listDefinitions(): Promise<AgentMemoryDefinitionDto[]> {
    return this.get<AgentMemoryDefinitionDto[]>('/v1/memory/definitions');
  }

  /** One definition with its full version history. */
  async getDefinition(id: string): Promise<MemoryDefinitionDetailDto> {
    return this.get<MemoryDefinitionDetailDto>(`/v1/memory/definitions/${id}`);
  }

  /** One exact historical version. */
  async getDefinitionVersion(id: string, version: number): Promise<AgentMemoryDefinitionDto> {
    return this.get<AgentMemoryDefinitionDto>(`/v1/memory/definitions/${id}/versions/${version}`);
  }

  /** Creates version 1 of a new definition for one platform agent. */
  async createDefinition(request: CreateMemoryDefinitionRequest): Promise<AgentMemoryDefinitionDto> {
    return this.post<AgentMemoryDefinitionDto>('/v1/memory/definitions', request);
  }

  /** Creates the next version of a definition (a change is a new row, never an update). */
  async createDefinitionVersion(
    id: string,
    request: MemoryDefinitionVersionRequest
  ): Promise<AgentMemoryDefinitionDto> {
    return this.post<AgentMemoryDefinitionDto>(`/v1/memory/definitions/${id}/versions`, request);
  }

  /** Clones one definition onto agents that have none. */
  async bulkApply(id: string, request: BulkApplyRequest): Promise<BulkApplyResultDto> {
    return this.post<BulkApplyResultDto>(`/v1/memory/definitions/${id}/bulk-apply`, request);
  }

  /**
   * Admin retrieval preview — same seam as runtime POST /v1/memory/context, called with
   * tenant-admin credentials so the UI never hits the harness runtime route.
   */
  async previewContext(
    id: string,
    request: PreviewMemoryContextRequest
  ): Promise<MemoryContextResponseDto> {
    return this.post<MemoryContextResponseDto>(`/v1/memory/definitions/${id}/preview`, request);
  }

  /** Reconstructs what the preview run injected (or the nothing-injected sentinel). */
  async getPreviewAudit(id: string, contextId: string): Promise<InjectionAuditDto[]> {
    return this.get<InjectionAuditDto[]>(
      `/v1/memory/definitions/${id}/preview/${encodeURIComponent(contextId)}/audit`
    );
  }

  // === Governance: SME rules and the review queue (§10A.4) ===

  /** Rules of one definition: current versions plus drafts awaiting review. */
  async listRules(definitionId: string): Promise<ProceduralRuleDto[]> {
    return this.get<ProceduralRuleDto[]>(`/v1/memory/rules?definitionId=${definitionId}`);
  }

  /** Append-only §12.2 lift windows for one family. */
  async getRuleEfficacy(familyId: string, definitionId: string): Promise<RuleEfficacyDto[]> {
    return this.get<RuleEfficacyDto[]>(
      `/v1/memory/rules/${familyId}/efficacy?definitionId=${encodeURIComponent(definitionId)}`
    );
  }

  /** Authors a new SME rule. Lands Draft; approval happens on the safety-gate queue. */
  async createRule(request: CreateRuleRequest): Promise<ProceduralRuleDto> {
    return this.post<ProceduralRuleDto>('/v1/memory/rules', request);
  }

  /** Authors a new version of an existing family. */
  async createRuleVersion(
    familyId: string,
    request: CreateRuleVersionRequest
  ): Promise<ProceduralRuleDto> {
    return this.post<ProceduralRuleDto>(`/v1/memory/rules/${familyId}/versions`, request);
  }

  /** Retires the current version of a family. */
  async retireRule(
    familyId: string,
    agentMemoryDefinitionId: string,
    reason: string
  ): Promise<ProceduralRuleDto> {
    return this.post<ProceduralRuleDto>(`/v1/memory/rules/${familyId}/retire`, {
      agentMemoryDefinitionId,
      reason,
    });
  }

  /** The review queue (empty until the Phase 3 write path lands). */
  async listCandidates(params?: {
    state?: string;
    impact?: number;
    definitionId?: string;
    limit?: number;
  }): Promise<MemoryCandidateDto[]> {
    const query = new URLSearchParams();
    // Empty string is meaningful: it asks for ALL states (the API defaults to "pending" otherwise).
    if (params?.state != null) query.append('state', params.state);
    if (params?.impact != null) query.append('impact', String(params.impact));
    if (params?.definitionId) query.append('definitionId', params.definitionId);
    if (params?.limit != null) query.append('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.get<MemoryCandidateDto[]>(`/v1/memory/candidates${suffix}`);
  }

  /** The effective tenant memory policy — read-only. */
  async getPolicy(): Promise<TenantMemoryPolicyDto> {
    return this.get<TenantMemoryPolicyDto>('/v1/memory/policy');
  }

  // === Write path: the extraction pipeline (Phase 3) ===

  /** Newest-first outbox rows for one definition — the Memory Pipeline page read. */
  async listOutbox(definitionId: string, take = 100): Promise<ExtractionOutboxDto[]> {
    return this.get<ExtractionOutboxDto[]>(
      `/v1/memory/outbox?definitionId=${encodeURIComponent(definitionId)}&take=${take}`
    );
  }

  /** The current late-bound outcome for one session. */
  async getSessionOutcome(sessionId: string): Promise<SessionMemoryOutcomeDto> {
    return this.get<SessionMemoryOutcomeDto>(`/v1/memory/sessions/${sessionId}/outcome`);
  }

  /** Assigns or revises the late-bound outcome. */
  async assignSessionOutcome(
    sessionId: string,
    request: {
      outcome: string;
      outcomeSource: string;
      failureReason?: string;
    }
  ): Promise<OutcomeReceiptDto> {
    return this.post<OutcomeReceiptDto>(`/v1/memory/sessions/${sessionId}/outcome`, request);
  }

  /** Reconstructs what was injected and which rules were live at asOf (§10A.5). */
  async getDecisionReconstruction(
    sessionId: string,
    asOf?: string
  ): Promise<DecisionReconstructionDto> {
    const qs = asOf ? `&asOf=${encodeURIComponent(asOf)}` : '';
    return this.get<DecisionReconstructionDto>(
      `/v1/memory/audit/decisions?sessionId=${encodeURIComponent(sessionId)}${qs}`
    );
  }

  // === Admin-only: quarantine + erasure (§6A.3, §7.3.1) ===

  /** The quarantine queue — unredacted by design; the API is AdminOnly. */
  async listQuarantine(limit = 100): Promise<MemoryCandidateDto[]> {
    return this.get<MemoryCandidateDto[]>(`/v1/memory/quarantine?limit=${limit}`);
  }

  /** Discards a quarantined candidate, or corrects its text and re-runs verification. */
  async resolveQuarantine(
    id: number,
    action: 'discard' | 'correct',
    correctedContent?: string
  ): Promise<MemoryCandidateDto> {
    return this.post<MemoryCandidateDto>(`/v1/memory/quarantine/${id}/resolve`, {
      action,
      correctedContent,
    });
  }

  /** Erases one subject across the memory stores. Returns counts only. */
  async eraseSubject(subjectIdentifier: string): Promise<MemoryErasureResultDto> {
    return this.post<MemoryErasureResultDto>('/v1/memory/erasure', { subjectIdentifier });
  }
}

export const memoryModuleService = new MemoryModuleService();
